import { useCallback, useEffect, useMemo, useState } from 'react'
import { createContainer } from 'unstated-next'

import type {
  KitColoredImage,
  ProductCatalogImage,
  ProductCatalogVideo,
} from '../../../../../../../__generated__/graphql/catalog/graphql'

export type GallerySlide = {
  __typename: 'CustomSlide'
  children: React.ReactNode
  key: string
}

export type GalleryImage = {
  __typename: NonNullable<ProductCatalogImage['__typename']>
  url: ProductCatalogImage['url']
  altText: ProductCatalogImage['altText']
}

export type GalleryKitImage = {
  __typename: NonNullable<KitColoredImage['__typename']>
  url: KitColoredImage['url']
  altText: KitColoredImage['altText']
}

export type GalleryVideo = {
  __typename: NonNullable<ProductCatalogVideo['__typename']>
  url: ProductCatalogVideo['url']
  type: ProductCatalogVideo['type']
}

export type GalleryAsset = GalleryImage | GalleryKitImage | GalleryVideo | GallerySlide

interface IProps {
  currentAssetIndex: number
  detailImages: Array<GalleryImage> | null | undefined
  images: Array<GalleryImage | GalleryKitImage | GallerySlide> | null | undefined
  imageDescription: string | undefined
  isImageZoomShown: boolean
  videos: Array<GalleryVideo> | null | undefined
}

const defaultProps: IProps = {
  currentAssetIndex: 0,
  detailImages: [],
  images: [],
  imageDescription: undefined,
  isImageZoomShown: false,
  videos: [],
}

function useGalleryImageState({
  currentAssetIndex: initialAssetIndex = defaultProps.currentAssetIndex,
  detailImages = defaultProps.detailImages,
  images = defaultProps.images,
  imageDescription = defaultProps.imageDescription,
  isImageZoomShown: initialIsImageZoomShown = defaultProps.isImageZoomShown,
  videos = defaultProps.videos,
}: Partial<IProps> = defaultProps) {
  const allAssets = useMemo<Array<GalleryAsset>>(() => {
    return [...(images ?? []), ...(detailImages ?? []), ...(videos ?? [])]
  }, [detailImages, images, videos])

  const [currentAssetIndex, setCurrentAssetIndex] = useState(initialAssetIndex)
  const [isImageZoomShown, setIsImageZoomShown] = useState(initialIsImageZoomShown)

  function showImageZoom(index?: number): void {
    setCurrentAssetIndex(index ?? currentAssetIndex)
    setIsImageZoomShown(true)
  }

  const hideImageZoom = useCallback((): void => {
    setIsImageZoomShown(false)
  }, [])

  useEffect(() => {
    if (allAssets.length === 0) return

    const nextAssetIndex = Math.min(currentAssetIndex, allAssets.length - 1)

    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (nextAssetIndex !== currentAssetIndex) {
      // TODO: Fix this auto-generated suppression.
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state, react-you-might-not-need-an-effect/no-chain-state-updates
      setCurrentAssetIndex(nextAssetIndex)
    }
  }, [allAssets.length, currentAssetIndex])

  return {
    allAssets,
    currentAssetIndex,
    hideImageZoom,
    imageDescription,
    isImageZoomShown,
    setCurrentAssetIndex,
    showImageZoom,
  }
}

export const GalleryImageStateContainer = createContainer(useGalleryImageState)
